import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiFetch } from "../utils/api";

// Refonte du rapport de triage (demande Jordan, 27/07/2026) — tableau
// simplifié à 7 colonnes, remplace l'ancien rapport HTML (supprimé le
// 28/10/2026 ; son lien vers l'export CSV est repris ici, bouton
// « Exporter CSV »). Spec complète : docs/tasks/PLAN_DE_TRAVAIL.md, P1-C.

const Q1_COLUMNS = [
  { key: "q1_operateur", label: "Q1op", title: "Q1 opérateur — débit mesuré/saisi par l'opérateur" },
  { key: "q1_heuristique", label: "Q1he", title: "Q1 heuristique — débit calculé par l'algorithme heuristique" },
  { key: "q1_manuel", label: "Q1man", title: "Q1 manuel — valeur corrigée manuellement" },
  { key: "q1_ia", label: "Q1IA", title: "Q1 IA — débit calculé par le modèle d'IA" },
];

const CVHE_TITLE =
  "Cvhe — écart relatif entre Q1 opérateur et Q1 heuristique : (Q1op − Q1he) / Q1op";

// Désaccord de catégorie : opérateur, IA et heuristique uniquement — le
// manuel est une référence absolue, exclu de la comparaison (demande
// Jordan, 27/07/2026).
const DISAGREEMENT_KEYS = ["q1_operateur", "q1_ia", "q1_heuristique"];

// Ordre de préférence pour le regroupement par défaut, faute d'une "valeur
// retenue" transmise par ce tableau léger (contrairement à compute_triage).
const CATEGORY_RANK_ORDER = ["q1_heuristique", "q1_operateur", "q1_ia", "q1_manuel"];

function categoryOf(q1) {
  if (q1 === null || q1 === undefined) return null;
  if (q1 < 30) return "rouge";
  if (q1 < 60) return "orange";
  return "vert";
}

const CATEGORY_CLASS = {
  rouge: "bg-red-100 text-red-800",
  orange: "bg-orange-100 text-orange-800",
  vert: "bg-green-100 text-green-800",
};

function fmtQ1(v) {
  return v === null || v === undefined ? "-" : String(Math.round(v));
}

// CvIA / CvIAHe (demande Benoît, 02/11/2026) : même règle que Cvhe, Cv{A}{B} = (A − B) / A.
const CVIA_TITLE = "CvIA — écart relatif entre Q1 opérateur et Q1 IA : (Q1op − Q1IA) / Q1op";
const CVIAHE_TITLE = "CvIAHe — écart relatif entre Q1 IA et Q1 heuristique : (Q1IA − Q1he) / Q1IA";

function fmtCvhe(v) {
  if (v === null || v === undefined) return "-";
  const pct = (v * 100).toFixed(1);
  return v > 0 ? `+${pct}%` : `${pct}%`;
}

function disagrees(row) {
  const cats = DISAGREEMENT_KEYS.map((k) => categoryOf(row[k])).filter((c) => c !== null);
  if (cats.length < 2) return false;
  return new Set(cats).size > 1;
}

function categoryRank(row) {
  for (const k of CATEGORY_RANK_ORDER) {
    const c = categoryOf(row[k]);
    if (c) return c === "rouge" ? 0 : c === "orange" ? 1 : 2;
  }
  return 3;
}

function insee5(codeSdis) {
  return (codeSdis || "").slice(0, 5);
}

// Libellés courts des champs Kizeo renvoyés par la réinitialisation d'une entrée.
const KIZEO_FIELD_LABELS = {
  answer_time: "date de réponse",
  pression_statique_en_mce: "Ps",
  pression_dynamique_en_mce: "Pd",
  debit_en_m3_hr: "débit",
  alpah: "alpha",
  code_sdis66: "N° PI",
  type_de_controle: "type de contrôle",
  pi_en_eau1: "PI en eau",
  photo_gros_plan_du_pi: "photo",
};

// Message affiché après "Réinitialiser Kizeo" (réponse de /kizeo-entry/.../reset).
function describeKizeoReset(formId, res) {
  const changes = (res.changes || []).map(
    (c) => `${KIZEO_FIELD_LABELS[c.field] || c.field} : ${c.old ?? "—"} → ${c.new ?? "—"}`
  );
  const what = changes.length
    ? `${changes.length} champ${changes.length > 1 ? "s" : ""} modifié${changes.length > 1 ? "s" : ""} (${changes.join(", ")})`
    : "aucun changement côté Kizeo";
  if (res.needs_reassociation) {
    return {
      type: "warning",
      text: `Entrée ${formId} retéléchargée : ${what}. ⚠️ ${res.reassociation_reasons.join(", ")} — `
        + "relancez l'association (Gestion > Récupération des données) pour la rattacher au bon événement.",
    };
  }
  const triageNote = res.triage === "kept"
    ? " Mesures Syrinix indisponibles : ligne de triage non recalculée."
    : "";
  return { type: "success", text: `Entrée ${formId} retéléchargée : ${what}.${triageNote}` };
}

export default function RapportTriage() {
  const [searchParams] = useSearchParams();
  const siteId = searchParams.get("site_id") || "";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [insee, setInsee] = useState("");
  const [sort, setSort] = useState({ key: null, dir: "asc" });

  // Réinitialisation d'une entrée Kizeo (27/10/2026) : formulaire en cours, message résultat,
  // ligne mise en évidence après rechargement.
  const [resettingId, setResettingId] = useState(null);
  const [resetMessage, setResetMessage] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [recomputing, setRecomputing] = useState(false);

  const loadTable = useCallback(
    () => apiFetch(`/api/streamlit/report/triage/${siteId}/table`).then(setData),
    [siteId]
  );

  useEffect(() => {
    if (!siteId) {
      setError("Aucun site_id fourni dans l'URL.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    loadTable()
      .catch((e) => setError(e.message || "Erreur de chargement"))
      .finally(() => setLoading(false));
  }, [siteId, loadTable]);

  // « Recalculer » (STRUCT-2, 29/10/2026) : calcul complet du site côté serveur — peut prendre
  // plusieurs minutes sur un gros site (Syrinix + IA par formulaire).
  const recompute = async () => {
    setRecomputing(true);
    setResetMessage(null);
    try {
      const res = await apiFetch(`/api/streamlit/report/triage/${siteId}/recompute`, { method: "POST" });
      setData(res);
      setResetMessage({ type: "success", text: "Triage recalculé." });
    } catch (e) {
      setResetMessage({ type: "error", text: `Recalcul impossible : ${e.message}` });
    } finally {
      setRecomputing(false);
    }
  };

  const resetKizeo = async (formId) => {
    const ok = window.confirm(
      `Retélécharger l'entrée Kizeo ${formId} ?\n\n`
      + "Ses données locales sont remplacées par la version actuelle de Kizeo, puis sa ligne "
      + "de triage est recalculée. Les autres formulaires ne sont pas touchés."
    );
    if (!ok) return;
    setResettingId(formId);
    setResetMessage(null);
    try {
      const res = await apiFetch(`/api/streamlit/kizeo-entry/${siteId}/${formId}/reset`, { method: "POST" });
      setResetMessage(describeKizeoReset(formId, res));
      await loadTable();
      setHighlightId(formId);
    } catch (e) {
      setResetMessage({ type: "error", text: `Réinitialisation de ${formId} impossible : ${e.message}` });
    } finally {
      setResettingId(null);
    }
  };

  const rows = data?.rows || [];

  const filtered = useMemo(() => {
    const searchLower = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (searchLower && !r.code_sdis.toLowerCase().includes(searchLower)) return false;
      if (insee && insee5(r.code_sdis) !== insee) return false;
      return true;
    });
  }, [rows, search, insee]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    if (sort.key) {
      arr.sort((a, b) => {
        const av = a[sort.key];
        const bv = b[sort.key];
        if (av === null || av === undefined) return bv === null || bv === undefined ? 0 : 1;
        if (bv === null || bv === undefined) return -1;
        if (av < bv) return sort.dir === "asc" ? -1 : 1;
        if (av > bv) return sort.dir === "asc" ? 1 : -1;
        return 0;
      });
    } else {
      // Regroupement par défaut : désaccords d'abord, puis 0-29, 30-59, 60+.
      arr.sort((a, b) => {
        const da = disagrees(a) ? 0 : 1;
        const db = disagrees(b) ? 0 : 1;
        if (da !== db) return da - db;
        return categoryRank(a) - categoryRank(b);
      });
    }
    return arr;
  }, [filtered, sort]);

  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return { key: null, dir: "asc" }; // 3e clic -> retour au regroupement par défaut
    });
  };

  const sortIndicator = (key) => {
    if (sort.key !== key) return "";
    return sort.dir === "asc" ? " ▲" : " ▼";
  };

  const openStreamlit = (formId) => {
    const rawUrl = `/streamlit/export_measure_v3/${siteId}/${formId}`;
    const normUrl = `/streamlit/export_measure_normalized_v3/${siteId}/${formId}`;
    const baseVis = new URL("/visualisation/", window.location.origin);
    baseVis.searchParams.set("csv_raw", rawUrl);
    baseVis.searchParams.set("csv_norm", normUrl);
    baseVis.searchParams.set("form_id", formId);
    baseVis.searchParams.set("site_id", siteId);
    window.open(baseVis.toString(), "_blank", "noopener,noreferrer");
  };

  const invalidForms = data?.invalid_forms || [];
  const noMatchForms = data?.no_match_forms || [];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-xl font-semibold mb-1">Rapport de triage — {siteId}</h1>
      <p className="text-sm text-gray-500 mb-4">
        {rows.length} formulaire{rows.length !== 1 ? "s" : ""}
        {fmtDateTime(data?.generated_at) && (
          <> · calculé le {fmtDateTime(data.generated_at)}</>
        )}
        {fmtDateTime(data?.updated_at) && (
          <> · mis à jour le {fmtDateTime(data.updated_at)}</>
        )}
      </p>

      {loading && <p className="text-gray-500">Chargement…</p>}
      {error && <p className="text-red-600">{error}</p>}

      {!loading && !error && (
        <>
          {resetMessage && (
            <div
              className={`mb-4 px-3 py-2 text-sm rounded border flex items-start gap-3 ${
                resetMessage.type === "error"
                  ? "bg-red-50 border-red-200 text-red-700"
                  : resetMessage.type === "warning"
                    ? "bg-amber-50 border-amber-200 text-amber-800"
                    : "bg-green-50 border-green-200 text-green-800"
              }`}
            >
              <span className="flex-1">{resetMessage.text}</span>
              <button type="button" onClick={() => setResetMessage(null)} className="opacity-60 hover:opacity-100">
                ✕
              </button>
            </div>
          )}

          <div className="flex flex-wrap gap-3 mb-4">
            <input
              type="text"
              placeholder="Rechercher un N° PI"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded w-56"
            />
            <input
              type="text"
              placeholder="Filtrer par code INSEE"
              value={insee}
              onChange={(e) => setInsee(e.target.value.replace(/\D/g, "").slice(0, 5))}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded w-48"
            />
            {(search || insee) && (
              <button
                type="button"
                onClick={() => { setSearch(""); setInsee(""); }}
                className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700"
              >
                Réinitialiser
              </button>
            )}
            {/* Export CSV complet du site (toutes les sources, Cvhe inclus — spec P1-C),
                indépendant des filtres ci-dessus. */}
            <button
              type="button"
              onClick={recompute}
              disabled={recomputing}
              title="Recalcule le triage de tout le site (peut prendre plusieurs minutes)"
              className="ml-auto px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              {recomputing ? "Recalcul en cours…" : "Recalculer"}
            </button>
            <a
              href={`/api/streamlit/report/triage/${encodeURIComponent(siteId)}/csv`}
              className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
            >
              Exporter CSV
            </a>
          </div>

          <div className="overflow-x-auto border border-gray-200 rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <Th onClick={() => toggleSort("code_sdis")}>N° PI{sortIndicator("code_sdis")}</Th>
                  <Th onClick={() => toggleSort("date")}>Date{sortIndicator("date")}</Th>
                  {Q1_COLUMNS.map((c) => (
                    <Th key={c.key} title={c.title} onClick={() => toggleSort(c.key)}>
                      {c.label}{sortIndicator(c.key)}
                    </Th>
                  ))}
                  <Th title={CVHE_TITLE} onClick={() => toggleSort("cvhe")}>Cvhe{sortIndicator("cvhe")}</Th>
                  <Th title={CVIA_TITLE} onClick={() => toggleSort("cvia")}>CvIA{sortIndicator("cvia")}</Th>
                  <Th title={CVIAHE_TITLE} onClick={() => toggleSort("cviahe")}>CvIAHe{sortIndicator("cviahe")}</Th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr
                    key={r.form_id}
                    className={`border-b border-gray-100 last:border-0 hover:bg-gray-50 ${
                      disagrees(r) ? "ring-1 ring-inset ring-amber-300" : ""
                    } ${highlightId === r.form_id ? "bg-violet-50" : ""}`}
                  >
                    <td className="px-3 py-1.5 font-mono">{r.code_sdis || "-"}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{r.date || "-"}</td>
                    {Q1_COLUMNS.map((c) => {
                      const cat = categoryOf(r[c.key]);
                      return (
                        <td key={c.key} className="px-3 py-1.5">
                          <span className={`inline-block px-2 py-0.5 rounded ${cat ? CATEGORY_CLASS[cat] : "text-gray-400"}`}>
                            {fmtQ1(r[c.key])}
                          </span>
                        </td>
                      );
                    })}
                    <td className="px-3 py-1.5">{fmtCvhe(r.cvhe)}</td>
                    <td className="px-3 py-1.5">{fmtCvhe(r.cvia)}</td>
                    <td className="px-3 py-1.5">{fmtCvhe(r.cviahe)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => openStreamlit(r.form_id)}
                        className="px-2 py-0.5 text-xs rounded border border-violet-500 text-violet-600 hover:bg-violet-50"
                      >
                        Ouvrir
                      </button>
                      <ResetKizeoButton
                        formId={r.form_id}
                        resettingId={resettingId}
                        onReset={resetKizeo}
                      />
                    </td>
                  </tr>
                ))}
                {sorted.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-3 py-6 text-center text-gray-400">
                      Aucun formulaire ne correspond aux filtres.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {invalidForms.length > 0 && (
            <section className="mt-8">
              <h2 className="text-base font-semibold mb-1">Essais invalides ({invalidForms.length})</h2>
              <p className="text-xs text-gray-500 mb-2">
                Exclus du triage (données aberrantes ou essais "Mise en Eau" sans mesure).
              </p>
              <ul className="text-sm list-disc list-inside text-gray-700">
                {invalidForms.map((entry) => {
                  const formId = typeof entry === "object" ? entry.form_id : entry;
                  const reason = typeof entry === "object" ? entry.reason : null;
                  return (
                    <li key={formId}>
                      {formId}
                      {reason && <span className="text-gray-400"> — {reason}</span>}
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {noMatchForms.length > 0 && (
            <section className="mt-8">
              <h2 className="text-base font-semibold mb-1">PI sans association Syrinix ({noMatchForms.length})</h2>
              <p className="text-xs text-gray-500 mb-2">
                Testés mais aucun événement d'ouverture Syrinix correspondant n'a été trouvé.
              </p>
              <table className="min-w-full text-sm border border-gray-200 rounded">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Form ID</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Date essai</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Type contrôle</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {noMatchForms.map((m) => (
                    <tr key={m.form_id} className="border-b border-gray-100 last:border-0">
                      <td className="px-3 py-1.5 font-mono">{m.form_id || "-"}</td>
                      <td className="px-3 py-1.5">{m.answer_time || m.photo_time || "-"}</td>
                      <td className="px-3 py-1.5">{m.type_de_controle || "-"}</td>
                      <td className="px-3 py-1.5">
                        <button
                          type="button"
                          onClick={() => openStreamlit(m.form_id)}
                          className="px-2 py-0.5 text-xs rounded border border-violet-500 text-violet-600 hover:bg-violet-50"
                        >
                          Ouvrir
                        </button>
                        <ResetKizeoButton
                          formId={m.form_id}
                          resettingId={resettingId}
                          onReset={resetKizeo}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </>
      )}
    </div>
  );
}

// "Réinitialiser Kizeo" : désactivé pendant qu'une autre entrée est en cours de réinitialisation
// (une seule à la fois, le backend refuse aussi pendant un ingest ou une association).
function ResetKizeoButton({ formId, resettingId, onReset }) {
  const busy = resettingId === formId;
  return (
    <button
      type="button"
      onClick={() => onReset(formId)}
      disabled={resettingId !== null}
      title="Retélécharger cette entrée depuis Kizeo et recalculer sa ligne"
      className="ml-1.5 px-2 py-0.5 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
    >
      {busy ? "Kizeo…" : "↻ Kizeo"}
    </button>
  );
}

// « 29/10/2026 à 09:14 » (heure locale du navigateur) — date de calcul du triage (STRUCT-2).
function fmtDateTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString("fr-FR");
  const time = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  return `${date} à ${time}`;
}

function Th({ onClick, title, children }) {
  return (
    <th
      onClick={onClick}
      title={title}
      className="px-3 py-2 text-left font-medium text-gray-600 cursor-pointer select-none hover:text-gray-900 whitespace-nowrap"
    >
      {children}
    </th>
  );
}
